import { useCallback, useRef } from 'react';
import { Form, FormRenderProps } from 'react-final-form';
import { connect } from 'react-redux';

import { composeValidators } from 'CorePackage/MainModule/validators/composeValidators';
import { makeIsNormalInteger } from 'CorePackage/MainModule/validators/isNormalInteger';
import { makeMaxNumber } from 'CorePackage/MainModule/validators/maxNumber';
import { Button } from 'UIPackage/MainModule/components/Button';
import { Modal } from 'UIPackage/ModalModule/components/Modal';
import { TextInputField } from 'UIPackage/MainModule/components/TextInputField';
import { getLawInterface } from 'CorePackage/MainModule/reducers/coreReducer';
import { LawInterfacesEnum } from 'CorePackage/MainModule/enums/LawInterfacesEnum';
import { makeIsNormalFloat } from 'CorePackage/MainModule/validators/isNormalFloat';
import { ModalHeader } from 'UIPackage/ModalModule/components/ModalHeader';
import { ModalBody } from 'UIPackage/ModalModule/components/ModalBody';
import { ModalActions } from 'UIPackage/ModalModule/components/ModalActions';
import { IRootState } from 'CorePackage/MainModule/interfaces/IRootState';

import styles from './UpdateKnmsDurationModal.module.scss';

interface IUpdateKnmsDurationForm {
  durationDays?: string;
  durationHours?: string;
}

type IUpdateKnmsDurationFormRenderProps = FormRenderProps<IUpdateKnmsDurationForm>;

export interface IUpdateKnmsDurationModalProps {
  visible?: boolean;
  knmsNumber: number;
  onApply: (durationDays?: string, durationHours?: string) => void;
  onClose: () => void;
}

interface IUpdateKnmsDurationModalStoreProps {
  isErknm: boolean;
}

const UpdateKnmsDurationModalWithoutStore = ({
  visible,
  knmsNumber,
  onApply,
  onClose,
  isErknm,
}: IUpdateKnmsDurationModalProps & IUpdateKnmsDurationModalStoreProps) => {
  const formRef = useRef<IUpdateKnmsDurationFormRenderProps['form']>();

  const handleApplyButtonClick = useCallback(() => formRef.current && formRef.current.submit(), []);

  const handleFormSubmit = useCallback(
    (values: IUpdateKnmsDurationForm) => {
      onApply(values.durationDays, values.durationHours?.replace(',', '.'));
    },
    [onApply],
  );

  const handleAfterClose = useCallback(() => formRef.current && formRef.current.reset(), []);

  const renderForm = useCallback(
    ({ form }: IUpdateKnmsDurationFormRenderProps) => {
      formRef.current = form;

      return (
        <form className={styles.Form}>
          <div className="shared-field-row">
            <div className={styles.FieldLabelBlock}>
              <label className={styles.FieldLabel} htmlFor="updateKnmsDurationDaysInput">
                Срок проведения (дней)
              </label>
            </div>
            <TextInputField
              className="shared-row-field"
              id="updateKnmsDurationDaysInput"
              name="durationDays"
              validate={composeValidators(
                makeIsNormalInteger('Введены недопустимые символы'),
                makeMaxNumber(365, 'Число не может превышать 365'),
              )}
            />
          </div>

          <div className={styles.DurationHoursFieldBlock}>
            <div className={styles.FieldLabelBlock}>
              <label className={styles.FieldLabel} htmlFor="updateKnmsDurationHoursInput">
                {isErknm ? 'Срок непосредственного взаимодействия (часов)' : 'Срок проведения (часов)'}
              </label>
            </div>
            <TextInputField
              className="shared-row-field"
              id="updateKnmsDurationHoursInput"
              name="durationHours"
              validate={composeValidators(
                makeIsNormalFloat('Введены недопустимые символы'),
                makeMaxNumber(256, 'Число не может превышать 256'),
              )}
            />
          </div>
        </form>
      );
    },
    [isErknm],
  );

  return (
    <Modal visible={visible} modalBodyClassName={styles.ModalBody} onClose={onClose} afterClose={handleAfterClose}>
      <ModalHeader title={`Изменение срока проведения КНМ (${knmsNumber})`} sizeText="small" />
      <ModalBody size="content" border="bottom">
        <Form<IUpdateKnmsDurationForm> render={renderForm} onSubmit={handleFormSubmit} />
      </ModalBody>
      <ModalActions>
        <Button text="Изменить" color="primary" size="medium" type="button" onClick={handleApplyButtonClick} />
        <Button text="Отменить" size="medium" onClick={onClose} />
      </ModalActions>
    </Modal>
  );
};

const mapStateToProps = (state: IRootState): IUpdateKnmsDurationModalStoreProps => ({
  isErknm: getLawInterface(state) === LawInterfacesEnum.FZ_248,
});

export const UpdateKnmsDurationModal = connect(mapStateToProps)(UpdateKnmsDurationModalWithoutStore);
